import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/auth-context'
import { isAuthError } from '@/lib/api-client'
import { setDiagnosticOwner } from '@/lib/staff-diagnostics'
import { t, useLanguage } from '@/lib/i18n'
import { ticketApi, type Summary } from './ticket-api'
import { snapshotTicketContext } from './ticket-context'
import { TicketComposer } from './TicketComposer'
import './staff-tickets.css'

interface StaffTickets { openReport: () => void; summary?: Summary; userId: string }
const Context = createContext<StaffTickets | null>(null)
export function useStaffTickets() { const value = useContext(Context); if (!value) throw new Error('Staff ticket boundary required'); return value }
export function StaffTicketActions() {
  useLanguage()
  const value = useContext(Context)
  if (!value) return null
  return <div className="staff-ticket-actions"><button type="button" onClick={(event) => { event.currentTarget.focus(); value.openReport() }}>{t('Report an issue')}</button><Link to="/admin/support">{t('My reports')}{value.summary?.unread_count ? <span aria-label={t('{0} unread reports', { '0': value.summary.unread_count })}> {value.summary.unread_count}</span> : null}</Link></div>
}
function StaffTicketSession({ userId }: { userId: string }) {
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const [context, setContext] = useState<ReturnType<typeof snapshotTicketContext> | null>(null)
  useEffect(() => { setDiagnosticOwner(userId); return () => setDiagnosticOwner(null) }, [userId])
  const summary = useQuery({ queryKey: ['staff-tickets', userId, 'summary'], queryFn: ticketApi.summary,
    refetchInterval: (query) => isAuthError(query.state.error) ? false : 45_000, refetchIntervalInBackground: false })
  const openReport = () => { if (!context) setContext(snapshotTicketContext(location)); setOpen(true) }
  return <Context.Provider value={{ userId, openReport, summary: summary.data }}>
    {/\/admin\/tests\/[^/]+\/preview\/?$/.test(location.pathname) && <aside className="staff-preview-support" aria-label={t('Staff preview support')}><StaffTicketActions /></aside>}
    <Outlet />
    {context && <TicketComposer open={open} context={context} onClose={() => setOpen(false)} onDone={() => { setOpen(false); setContext(null) }} />}
  </Context.Provider>
}
export function StaffTicketBoundary(): ReactNode {
  const { profile, demo } = useAuth()
  if (!profile || profile.user.role !== 'admin' || demo) return <Outlet />
  return <StaffTicketSession key={profile.user.id} userId={profile.user.id} />
}
